/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable security/detect-object-injection */
/* The schema is an arbitrary user-controlled JSON object (a JSON Schema
 * fragment). Indexing into ``properties[name]`` and ``properties[oldName]``
 * is the whole point of the editor; the keys come from the workflow itself,
 * not from any external input, so the security/detect-object-injection rule
 * is a false positive throughout this file. */
/**
 * Structured editor for an MCP-trigger's ``mcpToolInputSchema``.
 *
 * Two modes the author can toggle between:
 *
 * - **Fields** — recursive UI for adding/removing properties with type,
 *   description, required flag, and type-specific config (string min/max
 *   length, number bounds, object children, array items). Easy on-ramp
 *   for authors who don't think in JSON Schema.
 * - **JSON** — the original raw textarea, kept as the escape hatch for
 *   anything the Fields editor doesn't expose. Edits there flow back into
 *   the object that the Fields editor reads from.
 *
 * State model: the parent stores the schema as a plain object
 * (``mcpToolInputSchema``). Both modes mutate that same object, so
 * switching between them is loss-less — Fields → JSON serializes,
 * JSON → Fields parses. If the JSON is invalid the toggle is blocked
 * (mode stays JSON with an inline error) so we never feed a half-baked
 * object to the Fields recursion.
 *
 * Top-level schema shape is locked to ``{type:"object", properties, required}``
 * — that's what MCP tools/call expects for arguments. Inside, types can be
 * string / integer / number / boolean / object / array with arbitrary
 * nesting.
 */

import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { themeColor, xxllncColor } from '../../../layout/theme/color'

type SchemaObject = Record<string, unknown>

interface McpInputSchemaBuilderProps {
  value: SchemaObject | undefined
  onChange: (next: SchemaObject | undefined) => void
}

const SUPPORTED_TYPES = ['string', 'integer', 'number', 'boolean', 'object', 'array'] as const
type PropType = (typeof SUPPORTED_TYPES)[number]

const EMPTY_ROOT: SchemaObject = { type: 'object', properties: {}, required: [] }

/**
 * Normalise whatever the user (or a paste-in JSON) gave us into the
 * ``{type:"object", properties, required}`` shape the Fields UI expects.
 * Non-objects → empty root; missing fields → defaults. Loss-less when
 * the input already matches the shape.
 */
const normaliseRoot = (value: SchemaObject | undefined): SchemaObject => {
  if (!value || typeof value !== 'object') return { ...EMPTY_ROOT }
  const props =
    value.properties && typeof value.properties === 'object'
      ? (value.properties as SchemaObject)
      : {}
  const required = Array.isArray(value.required) ? (value.required as string[]) : []
  return { ...value, type: 'object', properties: props, required }
}

const McpInputSchemaBuilder: FC<McpInputSchemaBuilderProps> = ({ value, onChange }) => {
  const translate = useTranslate()
  const [mode, setMode] = useState<'fields' | 'json'>('fields')
  const [jsonText, setJsonText] = useState<string>(() =>
    value ? JSON.stringify(value, null, 2) : '',
  )
  const [jsonError, setJsonError] = useState<string | null>(null)

  // Keep JSON-text in sync when the parent updates the schema from outside
  // (e.g. another part of the editor mutates the trigger node). Only resync
  // while we're in Fields mode — typing in JSON mode shouldn't get clobbered.
  useEffect(() => {
    if (mode === 'fields') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '')
      setJsonError(null)
    }
  }, [value, mode])

  const root = useMemo(() => normaliseRoot(value), [value])
  const properties = (root.properties as SchemaObject) ?? {}
  const required = (root.required as string[]) ?? []

  const updateRoot = (next: SchemaObject) => onChange(next)

  const switchMode = (next: 'fields' | 'json') => {
    if (next === mode) return
    if (next === 'json') {
      // Always allowed: serialise the canonical object.
      setJsonText(value ? JSON.stringify(value, null, 2) : '')
      setJsonError(null)
      setMode('json')
      return
    }
    // next === 'fields': require the current JSON to be a valid object first.
    const trimmed = jsonText.trim()
    if (!trimmed) {
      onChange(undefined)
      setJsonError(null)
      setMode('fields')
      return
    }
    try {
      const parsed = JSON.parse(trimmed) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError(translate('workflow.config.mcpSchemaJsonNotAnObject'))
        return
      }
      onChange(parsed as SchemaObject)
      setJsonError(null)
      setMode('fields')
    } catch (err) {
      setJsonError((err as Error).message)
    }
  }

  const handleJsonBlur = () => {
    const trimmed = jsonText.trim()
    if (!trimmed) {
      onChange(undefined)
      setJsonError(null)
      return
    }
    try {
      const parsed = JSON.parse(trimmed) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError(translate('workflow.config.mcpSchemaJsonNotAnObject'))
        return
      }
      onChange(parsed as SchemaObject)
      setJsonError(null)
    } catch (err) {
      setJsonError((err as Error).message)
    }
  }

  const setProperty = (name: string, prop: SchemaObject) => {
    const nextProps = { ...properties, [name]: prop }
    updateRoot({ ...root, properties: nextProps })
  }

  const renameProperty = (oldName: string, newName: string) => {
    if (!newName || newName === oldName) return
    if (properties[newName]) return  // collision — silently ignore
    const nextProps: SchemaObject = {}
    Object.entries(properties).forEach(([k, v]) => {
      nextProps[k === oldName ? newName : k] = v
    })
    const nextRequired = required.map((r) => (r === oldName ? newName : r))
    updateRoot({ ...root, properties: nextProps, required: nextRequired })
  }

  const deleteProperty = (name: string) => {
    const nextProps = { ...properties }
    delete nextProps[name]
    const nextRequired = required.filter((r) => r !== name)
    updateRoot({ ...root, properties: nextProps, required: nextRequired })
  }

  const setRequired = (name: string, isRequired: boolean) => {
    const next = isRequired
      ? Array.from(new Set([...required, name]))
      : required.filter((r) => r !== name)
    updateRoot({ ...root, required: next })
  }

  const addProperty = () => {
    let i = 1
    let name = 'field'
    while (properties[name]) {
      i += 1
      name = `field${i}`
    }
    setProperty(name, { type: 'string' })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60 }}
        >
          {translate('workflow.config.mcpToolInputSchema')}
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(_e, val: 'fields' | 'json' | null) => val && switchMode(val)}
        >
          <ToggleButton value="fields" sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}>
            {translate('workflow.config.mcpSchemaModeFields')}
          </ToggleButton>
          <ToggleButton value="json" sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}>
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'json' ? (
        <Box>
          <TextField
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            onBlur={handleJsonBlur}
            size="small"
            fullWidth
            multiline
            minRows={6}
            error={Boolean(jsonError)}
            placeholder='{"type":"object","properties":{...},"required":[...]}'
            InputProps={{
              sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: jsonError ? xxllncColor.red100 : themeColor.slate60,
              mt: 0.5,
              display: 'block',
              lineHeight: 1.4,
            }}
          >
            {jsonError ?? translate('workflow.config.mcpToolInputSchemaHelp')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Object.entries(properties).length === 0 && (
            <Typography
              variant="caption"
              sx={{ color: themeColor.slate60, fontStyle: 'italic' }}
            >
              {translate('workflow.config.mcpSchemaNoFields')}
            </Typography>
          )}
          {Object.entries(properties).map(([name, prop]) => (
            <SchemaPropertyEditor
              key={name}
              name={name}
              property={(prop ?? {}) as SchemaObject}
              isRequired={required.includes(name)}
              siblingNames={Object.keys(properties).filter((n) => n !== name)}
              onRename={(next) => renameProperty(name, next)}
              onChange={(next) => setProperty(name, next)}
              onDelete={() => deleteProperty(name)}
              onToggleRequired={(req) => setRequired(name, req)}
              depth={0}
            />
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addProperty}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {translate('workflow.config.mcpSchemaAddField')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

// ── recursive single-property editor ─────────────────────────────────────

interface SchemaPropertyEditorProps {
  name: string
  property: SchemaObject
  isRequired: boolean
  depth: number
  /**
   * Names of the other properties at the same level. Used to surface a
   * rename-collision error inline instead of silently dropping the rename
   * (which is what happened before — see the early return in renameProperty).
   * Omit / pass [] for nameless slots (e.g. the array `items` editor).
   */
  siblingNames?: string[]
  onRename: (newName: string) => void
  onChange: (next: SchemaObject) => void
  onDelete: () => void
  onToggleRequired: (req: boolean) => void
}

const SchemaPropertyEditor: FC<SchemaPropertyEditorProps> = ({
  name,
  property,
  isRequired,
  depth,
  siblingNames = [],
  onRename,
  onChange,
  onDelete,
  onToggleRequired,
}) => {
  const translate = useTranslate()
  const [expanded, setExpanded] = useState<boolean>(depth === 0)
  const [localName, setLocalName] = useState<string>(name)
  useEffect(() => setLocalName(name), [name])

  const trimmedLocal = localName.trim()
  const nameError =
    trimmedLocal === name
      ? null
      : trimmedLocal === ''
        ? translate('workflow.config.mcpSchemaNameEmpty')
        : siblingNames.includes(trimmedLocal)
          ? translate('workflow.config.mcpSchemaNameCollision')
          : null

  const handleNameBlur = () => {
    if (nameError) {
      // Don't try to rename to an invalid value, but keep the bad value in
      // the input so the user sees the error and can fix it.
      return
    }
    if (trimmedLocal === name) return
    onRename(trimmedLocal)
  }

  const type = (property.type as PropType) ?? 'string'
  const description = (property.description as string) ?? ''

  const setField = (patch: SchemaObject) => onChange({ ...property, ...patch })

  const handleTypeChange = (next: PropType) => {
    // When switching type, strip fields that no longer apply so the JSON
    // stays clean. Keep `description` since that's universal.
    const cleaned: SchemaObject = {
      type: next,
      ...(description ? { description } : {}),
    }
    if (next === 'object') {
      cleaned.properties = (property.properties as SchemaObject) ?? {}
      cleaned.required = (property.required as string[]) ?? []
    }
    if (next === 'array') {
      cleaned.items = (property.items as SchemaObject) ?? { type: 'string' }
      if (property.minItems !== undefined) cleaned.minItems = property.minItems
      if (property.maxItems !== undefined) cleaned.maxItems = property.maxItems
    }
    if (next === 'string') {
      if (property.minLength !== undefined) cleaned.minLength = property.minLength
      if (property.maxLength !== undefined) cleaned.maxLength = property.maxLength
      if (property.format !== undefined) cleaned.format = property.format
      if (property.enum !== undefined) cleaned.enum = property.enum
    }
    if (next === 'integer' || next === 'number') {
      if (property.minimum !== undefined) cleaned.minimum = property.minimum
      if (property.maximum !== undefined) cleaned.maximum = property.maximum
    }
    onChange(cleaned)
  }

  // Nested-object handlers
  const nestedProps = (property.properties as SchemaObject) ?? {}
  const nestedRequired = (property.required as string[]) ?? []
  const setNestedProperty = (propName: string, prop: SchemaObject) => {
    setField({ properties: { ...nestedProps, [propName]: prop } })
  }
  const renameNestedProperty = (oldName: string, newName: string) => {
    if (!newName || newName === oldName || nestedProps[newName]) return
    const next: SchemaObject = {}
    Object.entries(nestedProps).forEach(([k, v]) => {
      next[k === oldName ? newName : k] = v
    })
    setField({
      properties: next,
      required: nestedRequired.map((r) => (r === oldName ? newName : r)),
    })
  }
  const deleteNestedProperty = (propName: string) => {
    const next = { ...nestedProps }
    delete next[propName]
    setField({
      properties: next,
      required: nestedRequired.filter((r) => r !== propName),
    })
  }
  const setNestedRequired = (propName: string, req: boolean) => {
    setField({
      required: req
        ? Array.from(new Set([...nestedRequired, propName]))
        : nestedRequired.filter((r) => r !== propName),
    })
  }
  const addNestedProperty = () => {
    let i = 1
    let propName = 'field'
    while (nestedProps[propName]) {
      i += 1
      propName = `field${i}`
    }
    setNestedProperty(propName, { type: 'string' })
  }

  // Array-items handler — items is itself a single (anonymous) property.
  const itemsSchema = (property.items as SchemaObject) ?? { type: 'string' }
  const setItems = (next: SchemaObject) => setField({ items: next })

  const indent = depth * 1.5
  const showDetails = expanded && (type === 'object' || type === 'array' || type === 'string' || type === 'integer' || type === 'number')

  return (
    <Box
      sx={{
        ml: indent,
        border: `1px solid ${themeColor.slate10}`,
        borderRadius: '6px',
        p: 1,
        background: depth > 0 ? themeColor.slate05 : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ p: 0.25 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
          <TextField
            label={translate('workflow.config.mcpSchemaFieldName')}
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
            error={Boolean(nameError)}
            helperText={nameError ?? undefined}
            size="small"
            placeholder="employee_search"
            InputLabelProps={{ shrink: true }}
            sx={{ 'flex': 1, '& input': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Tooltip title={translate('workflow.config.mcpSchemaRequired')} arrow>
            <Checkbox
              size="small"
              checked={isRequired}
              onChange={(e) => onToggleRequired(e.target.checked)}
              sx={{ p: 0.5 }}
            />
          </Tooltip>
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ pl: 4 }}>
          <TextField
            select
            label={translate('workflow.config.mcpSchemaFieldType')}
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as PropType)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          >
            {SUPPORTED_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {showDetails && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1, pl: 3 }}>
          <TextField
            label={translate('workflow.config.mcpSchemaFieldDescription')}
            value={description}
            onChange={(e) => setField({ description: e.target.value })}
            size="small"
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
          />

          {type === 'string' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label={translate('workflow.config.mcpSchemaMinLength')}
                type="number"
                value={(property.minLength as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    minLength:
                      e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
              <TextField
                label={translate('workflow.config.mcpSchemaMaxLength')}
                type="number"
                value={(property.maxLength as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    maxLength:
                      e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
              <TextField
                label={translate('workflow.config.mcpSchemaFormat')}
                value={(property.format as string | undefined) ?? ''}
                onChange={(e) =>
                  setField({ format: e.target.value === '' ? undefined : e.target.value })
                }
                size="small"
                sx={{ width: 140 }}
                placeholder="uuid / email / …"
              />
            </Box>
          )}

          {(type === 'integer' || type === 'number') && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={translate('workflow.config.mcpSchemaMin')}
                type="number"
                value={(property.minimum as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    minimum: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
              <TextField
                label={translate('workflow.config.mcpSchemaMax')}
                type="number"
                value={(property.maximum as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    maximum: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
            </Box>
          )}

          {type === 'array' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={translate('workflow.config.mcpSchemaMinItems')}
                type="number"
                value={(property.minItems as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    minItems:
                      e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
              <TextField
                label={translate('workflow.config.mcpSchemaMaxItems')}
                type="number"
                value={(property.maxItems as number | undefined) ?? ''}
                onChange={(e) =>
                  setField({
                    maxItems:
                      e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10),
                  })
                }
                size="small"
                sx={{ width: 110 }}
              />
            </Box>
          )}

          {type === 'object' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(nestedProps).map(([childName, child]) => (
                <SchemaPropertyEditor
                  key={childName}
                  name={childName}
                  property={(child ?? {}) as SchemaObject}
                  isRequired={nestedRequired.includes(childName)}
                  siblingNames={Object.keys(nestedProps).filter((n) => n !== childName)}
                  onRename={(next) => renameNestedProperty(childName, next)}
                  onChange={(next) => setNestedProperty(childName, next)}
                  onDelete={() => deleteNestedProperty(childName)}
                  onToggleRequired={(req) => setNestedRequired(childName, req)}
                  depth={depth + 1}
                />
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addNestedProperty}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {translate('workflow.config.mcpSchemaAddField')}
              </Button>
            </Box>
          )}

          {type === 'array' && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: themeColor.slate60, display: 'block', mb: 0.5 }}
              >
                {translate('workflow.config.mcpSchemaArrayItems')}
              </Typography>
              <SchemaPropertyEditor
                name="items"
                property={itemsSchema}
                isRequired={false}
                onRename={() => {
                  /* items has no name */
                }}
                onChange={setItems}
                onDelete={() => setItems({ type: 'string' })}
                onToggleRequired={() => {
                  /* items isn't toggle-required */
                }}
                depth={depth + 1}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default McpInputSchemaBuilder
